/**
 * Send-throttle helpers.
 *
 * `TickThrottle` — permit ≤ 1 send per tick-interval (ms). Used for `cast`
 * auto-repeat.
 *
 * The caller passes a monotonic `nowMs` (mapper tests use a fake clock).
 */

/** Tick-based throttle. Constructed with a tick rate and a `now` reader. */
export class TickThrottle {
  private readonly intervalMs: number;
  private lastSendAt: number;

  constructor(tickRateHz: number) {
    if (tickRateHz <= 0) {
      throw new Error(`TickThrottle: tickRateHz must be > 0, got ${String(tickRateHz)}`);
    }
    this.intervalMs = 1000 / tickRateHz;
    // -Infinity so the first call is never throttled.
    this.lastSendAt = Number.NEGATIVE_INFINITY;
  }

  /** Attempt to spend a slot; returns true if allowed and marks the
   *  slot spent, false if throttled. */
  tryConsume(nowMs: number): boolean {
    if (nowMs - this.lastSendAt >= this.intervalMs) {
      this.lastSendAt = nowMs;
      return true;
    }
    return false;
  }

  /** Reset — next call is always allowed. */
  reset(): void {
    this.lastSendAt = Number.NEGATIVE_INFINITY;
  }
}
